import { Alert, AlertTitle, Box, type SxProps, type Theme } from '@mui/material';

import { T } from '../../util/Localize';

export interface SuspendedAccountNoticeProps {
    /**
     * Merged over the default placement (full width, centred, top margin sized for a page). The
     * login form passes its own margin, since it sits inside a form that already spaces its rows.
     */
    sx?: SxProps<Theme>;
}

/**
 * Generic suspended-account message.
 *
 * Deliberately generic: `Dashboard/suspended.asp` is orphaned dead code containing 2020-era
 * litigation copy addressed to specific photographers, and is not to be reproduced. The live
 * legacy equivalent is `/inactiveaccount.asp` ("Account Inactive" / "This account has been
 * deactivated.").
 *
 * WHERE IT RENDERS (ROBO-478): the login screen (`pages/login/Login.tsx`), when the API refuses a
 * suspended business's non-Owner user a session — at sign-in, on the boot authorize, or on a
 * mid-session refresh. That is legacy's `/inactiveaccount.asp` branch. The dashboard does NOT render
 * it: the only suspended user with a session is the Owner, and legacy lets them use every page. See
 * `pages/dashboard/use-suspended-account.ts`.
 *
 * Lives in `components/errors` rather than `pages/dashboard` because the hub login page renders it,
 * and auth pages must not depend on studio page modules (`docs/ux-architecture.md`).
 */
export function SuspendedAccountNotice({ sx }: SuspendedAccountNoticeProps) {
    return (
        <Box
            data-testid="suspended-account-notice"
            sx={[{ width: '100%', mx: 'auto', mt: 4 }, ...(Array.isArray(sx) ? sx : [sx])]}
        >
            <Alert severity="warning">
                <AlertTitle>{T('Account Inactive')}</AlertTitle>
                {T('This account has been deactivated. Please contact support for assistance.')}
            </Alert>
        </Box>
    );
}
